export enum CMMBranch {
    GoalsDebt = 'goals-debt',
    GoalsSavings = 'goals-savings',
    GoalsSystem = 'goals-system',
    StartInvesting = 'start-investing',
    GoalsFire = 'goals-fire',
    /** @deprecated Replaced by GoalsSystem. Kept for historical thread data. */
    MoneySystem = 'money-system',
}

/** Conversational MM onboarding step inferred from persisted thread messages. */
export const CMM_CONVERSATION_STEPS = [
    'branch-selection',
    'savings-goal-type',
    'goal-collection',
    'debt-types',
    'debt-details',
    'goal-summary',
    'income-collection',
    'bills-estimation',
    'invest-household-type',
    'invest-filing-status',
    'invest-existing-accounts',
    'results',
] as const;

export type CMMConversationStep = (typeof CMM_CONVERSATION_STEPS)[number];

export enum WidgetType {
    AhaMoment = 'Aha Moment',
    SpendGoals = 'Spend Goals',
    MultiSelectInput = 'Multi Select Input',
    InlineDataTable = 'Inline Data Table',
    DraftedMessageButton = 'Drafted Message Button',
    GoalSummaryCard = 'Goal Summary Card',
    Chart = 'Chart',
    FreeToSpend = 'Free To Spend',
    MoneyMap = 'Money Map',
    IncomeSplit = 'Income Split',
    Transactions = 'Transactions',
    AccountBalances = 'Account Balances',
    CardDetails = 'Card Details',
    InfoCards = 'Info Cards',
    SendToGuide = 'Send To Guide',
    EmailSupport = 'Email Support',
    SliderInput = 'Slider Input',
    EstimatedBills = 'Estimated Bills',
    SavingsGoals = 'Savings Goals',
    SavingsGoalAmounts = 'Savings Goal Amounts',
    ExistingGoalsBadges = 'Existing Goals Badges',
    IncomeInput = 'Income Input',
    IncomeCollection = 'Income Collection',
    DebtBalanceSliders = 'Debt Balance Sliders',
    CreditCardRateCallout = 'Credit Card Rate Callout',
}

export interface DraftedMessageButtonWidgetProps {
    label: string;
    confirmationPrefix: string;
    confirmationPayload: Record<string, unknown>;
}

export interface GoalSummaryMetric {
    label: string;
    value: string;
}

export interface GoalSummaryCardWidgetProps {
    eyebrow?: string;
    title: string;
    metric: GoalSummaryMetric;
    /** Illustration shown beside the title and metric. Defaults to discover. */
    image?: AhaMomentImage;
}

export type InlineDataTableColumnFormat = 'text' | 'currency' | 'percent';

export interface InlineDataTableColumn {
    key: string;
    header: string;
    align?: 'left' | 'right';
    format?: InlineDataTableColumnFormat;
}

export interface InlineDataTableRow {
    label: string;
    cells: Record<string, string | number | null>;
    isTotalsRow?: boolean;
}

export enum EstimatedBillCategory {
    RentMortgage = 'rent_mortgage',
    Childcare = 'childcare',
    Utilities = 'utilities',
    Insurance = 'insurance',
    PhoneInternet = 'phone_internet',
    Subscriptions = 'subscriptions',
    DebtMinimums = 'debt_minimums',
}

export interface EstimatedBill {
    category: string;
    label: string;
    amountCents: number;
}

export interface EstimatedBillsWidgetProps {
    bills: EstimatedBill[];
    confirmationPrefix: string;
    confirmationPayload: Record<string, unknown>;
}

export type InfoCardId =
    | 'paycheck-has-a-job'
    | 'lower-debt-rates'
    | 'spend-with-confidence'
    | 'one-on-one-guidance';

export interface InfoCardItem {
    id: InfoCardId;
    title: string;
    description: string;
}

export interface InfoCardsWidgetProps {
    cards: InfoCardItem[];
}

/**
 * Predetermined slide content for the info carousel. The client renders these
 * by ID so the LLM doesn't need to supply titles/descriptions at runtime.
 */
export const INFO_CAROUSEL_SLIDES: Record<
    InfoCardId,
    Omit<InfoCardItem, 'id'>
> = {
    'paycheck-has-a-job': {
        title: 'Every paycheck has a job',
        description:
            'Bills, spending, savings, and goals\u2013automatically organized.',
    },
    'lower-debt-rates': {
        title: 'Lower your debt rates',
        description:
            'We can connect you to partners that may reduce interest rates',
    },
    'spend-with-confidence': {
        title: 'Spend with confidence',
        description: 'Know exactly what you can spend without second-guessing',
    },
    'one-on-one-guidance': {
        title: '1-on-1 guidance',
        description: 'Fruitful experts will build and refine your plan',
    },
};

export function resolveInfoCards(slideIds: InfoCardId[]): InfoCardItem[] {
    return slideIds.map((id) => ({ id, ...INFO_CAROUSEL_SLIDES[id] }));
}

/**
 * Configuration for a branch's pre-rendered opener. When present, the app
 * renders this content instantly instead of waiting for the LLM.
 */
export interface BranchOpener {
    text: string;
    slides: InfoCardId[];
    transitionText: string;
}

export interface InlineDataTableWidgetProps {
    columns: InlineDataTableColumn[];
    rows: InlineDataTableRow[];
    accountColumnHeader?: string;
    totalsRowLabel?: string;
}

export enum AhaMomentImage {
    Discover = 'discover.png',
    DiscoverBw = 'discover-bw.png',
    Investing = 'investing.png',
    Management = 'management.png',
    Roadmap = 'roadmap.png',
    SecureSavings = 'secure-savings.png',
}

export enum HeadlineType {
    DebtFreeBy = 'debt-free-by',
    SaveForGoalBy = 'save-for-goal-by',
    PrimaryGoal = 'primary-goal',
    InvestingBy = 'investing-by',
}

export enum GoalsDebtType {
    CreditCards = 'credit-cards',
    PersonalLoans = 'personal-loans',
    StudentLoans = 'student-loans',
    CarLoan = 'car-loan',
    Mortgage = 'mortgage',
    /** @deprecated Use CarLoan and Mortgage separately */
    CarLoanOrMortgage = 'car-loan-or-mortgage',
    /** Custom high-interest debt the member described in their own words. */
    Other = 'other',
    NoDebt = 'no-debt',
}

export const GOALS_DEBT_TYPE_OPTIONS = [
    { id: GoalsDebtType.CreditCards, label: 'Credit cards' },
    { id: GoalsDebtType.PersonalLoans, label: 'Personal loans' },
    { id: GoalsDebtType.StudentLoans, label: 'Student loans' },
    { id: GoalsDebtType.CarLoan, label: 'Car loan' },
    { id: GoalsDebtType.Mortgage, label: 'Mortgage' },
] as const;

export const HIGH_INTEREST_DEBT_OPTIONS = [
    { id: GoalsDebtType.CreditCards, label: 'Credit cards' },
    { id: GoalsDebtType.PersonalLoans, label: 'Personal loans' },
    { id: 'no-high-interest-debt', label: "I don't have high-interest debt" },
] as const;

export enum ExistingAccountType {
    Brokerage = 'brokerage',
    IRA = 'ira',
    RothIRA = 'roth-ira',
    EmployerRetirement = 'employer-retirement',
    NoExistingInvestments = 'no-existing-investments',
}

export const EXISTING_ACCOUNT_TYPE_OPTIONS = [
    { id: ExistingAccountType.Brokerage, label: 'Brokerage account' },
    { id: ExistingAccountType.IRA, label: 'Traditional IRA' },
    { id: ExistingAccountType.RothIRA, label: 'Roth IRA' },
    {
        id: ExistingAccountType.EmployerRetirement,
        label: '401(k) or 403(b)',
    },
    {
        id: ExistingAccountType.NoExistingInvestments,
        label: "I don't have any existing investments",
    },
] as const;

export const SAVINGS_GOAL_TYPE_OPTIONS = [
    { id: 'home-down-payment', label: 'Down payment' },
    { id: 'vacation-travel', label: 'Travel' },
    { id: 'new-car', label: 'New car' },
    { id: 'growing-family', label: 'Growing a family' },
    { id: 'charitable-giving', label: 'Charitable giving' },
    { id: 'gifts', label: 'Gifts' },
    { id: 'home-repairs', label: 'Home repairs' },
    { id: 'wedding', label: 'Wedding' },
    { id: 'education', label: 'Education' },
    { id: 'moving', label: 'Moving' },
] as const;

export interface SavingsGoalOption {
    id: string;
    label: string;
}

export interface SavingsGoalsWidgetProps {
    options: SavingsGoalOption[];
    maxSelections: number;
    allowCustomInput: boolean;
    customInputPlaceholder?: string;
    responseKey: string;
}

export interface SavingsGoalEstimate {
    goalId: string;
    label: string;
    targetAmountCents: number;
}

export interface SavingsGoalAmountsWidgetProps {
    goals: SavingsGoalEstimate[];
    confirmationPrefix: string;
    confirmationPayload: Record<string, unknown>;
}

export interface ExistingGoalsBadge {
    label: string;
    icon: string;
}

export interface ExistingGoalsBadgesWidgetProps {
    title: string;
    goals: ExistingGoalsBadge[];
}

export const FILING_STATUS_OPTIONS = [
    {
        id: 'married_filing_jointly',
        label: 'Married filing jointly',
    },
    {
        id: 'married_filing_separately',
        label: 'Married filing separately',
    },
    {
        id: 'single',
        label: 'Both filing single',
    },
    {
        id: 'not_sure',
        label: "I'm not sure",
    },
] as const;

export interface MoneyMapNumbers {
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
}

export enum ResultSlide {
    AhaMoment = 'aha-moment',
    MoneyMap = 'money-map',
    CoreAccount = 'core-account',
    SpendAccount = 'spend-account',
    GoalsIntro = 'goals-intro',
    FinancialHealth = 'financial-health',
    SavingsGoals = 'savings-goals',
    Goals = 'goals',
    InvestmentWaterfall = 'investment-waterfall',
    InvestmentsOverview = 'investments-overview',
    WhyInvest = 'why-invest',
    InvestingCta = 'investing-cta',
    WhatsNext = 'whats-next',
}

export const RESULT_SLIDE_DESCRIPTIONS: Record<ResultSlide, string> = {
    [ResultSlide.AhaMoment]:
        'The headline result (e.g. "Debt free by March 2028") with an illustration',
    [ResultSlide.MoneyMap]:
        'Visual breakdown of income into bills, spend, and goals',
    [ResultSlide.CoreAccount]:
        'Core account details: bills amount, how autopay works',
    [ResultSlide.SpendAccount]:
        'Spend account: discretionary budget, spending graph',
    [ResultSlide.GoalsIntro]:
        'Introduces the goals waterfall concept: how much goes to goals, how the waterfall works, and a preview of the first goal',
    [ResultSlide.Goals]: 'Goals waterfall: emergency fund target and timeline',
    [ResultSlide.FinancialHealth]:
        'Financial health goal set: debt payoff, full emergency fund, and retirement contributions (splits vary by debt type)',
    [ResultSlide.SavingsGoals]:
        'Savings goals: custom savings targets like "save for something" that are funded after financial health goals',
    [ResultSlide.InvestmentWaterfall]:
        'Investment waterfall: prioritized IRA and brokerage accounts with compound growth projection and interactive sliders',
    [ResultSlide.InvestmentsOverview]:
        'Product overview: managed portfolios, autopilot rebalancing, tax-loss harvesting',
    [ResultSlide.WhyInvest]:
        'Value prop: 0% management fee comparison table and historical performance',
    [ResultSlide.InvestingCta]:
        'Investing CTA: connect with a Guide to put the plan into action',
    [ResultSlide.WhatsNext]: 'CTA to connect with a guide',
};

export const BRANCH_SLIDES: Record<CMMBranch, ResultSlide[]> = {
    [CMMBranch.GoalsDebt]: [
        ResultSlide.MoneyMap,
        ResultSlide.CoreAccount,
        ResultSlide.SpendAccount,
        ResultSlide.GoalsIntro,
        ResultSlide.FinancialHealth,
        ResultSlide.Goals,
        ResultSlide.WhatsNext,
    ],
    [CMMBranch.GoalsSavings]: [
        ResultSlide.AhaMoment,
        ResultSlide.MoneyMap,
        ResultSlide.CoreAccount,
        ResultSlide.SpendAccount,
        ResultSlide.GoalsIntro,
        ResultSlide.FinancialHealth,
        ResultSlide.SavingsGoals,
        ResultSlide.Goals,
        ResultSlide.WhatsNext,
    ],
    [CMMBranch.GoalsSystem]: [
        ResultSlide.AhaMoment,
        ResultSlide.MoneyMap,
        ResultSlide.CoreAccount,
        ResultSlide.SpendAccount,
        ResultSlide.GoalsIntro,
        ResultSlide.FinancialHealth,
        ResultSlide.SavingsGoals,
        ResultSlide.Goals,
        ResultSlide.WhatsNext,
    ],
    [CMMBranch.StartInvesting]: [
        ResultSlide.AhaMoment,
        ResultSlide.InvestmentWaterfall,
        ResultSlide.InvestmentsOverview,
        ResultSlide.WhyInvest,
        ResultSlide.InvestingCta,
    ],
    [CMMBranch.MoneySystem]: [
        ResultSlide.MoneyMap,
        ResultSlide.CoreAccount,
        ResultSlide.SpendAccount,
        ResultSlide.GoalsIntro,
        ResultSlide.FinancialHealth,
        ResultSlide.SavingsGoals,
        ResultSlide.Goals,
        ResultSlide.WhatsNext,
    ],
    [CMMBranch.GoalsFire]: [
        ResultSlide.AhaMoment,
        ResultSlide.MoneyMap,
        ResultSlide.CoreAccount,
        ResultSlide.SpendAccount,
        ResultSlide.GoalsIntro,
        ResultSlide.FinancialHealth,
        ResultSlide.SavingsGoals,
        ResultSlide.Goals,
        ResultSlide.WhatsNext,
    ],
};

export const DEBT_BRANCH_SLIDES = BRANCH_SLIDES[CMMBranch.GoalsDebt];

/**
 * Individual debt entry for the simulation intake.
 *
 * Parallel types:
 * - Zod schema: llm-service `debtSchema` in ConversationalMMIntakeSchema.ts
 * - Builder: money-map-simulator `DebtIntakeEntry` in builders.ts (identical)
 */
export interface DebtIntakeEntry {
    description?: string | null;
    minimumPaymentCents: number;
    balanceRemainingCents?: number;
    annualInterestRatePercent?: number;
}

/**
 * Individual savings goal entry for the simulation intake.
 *
 * Parallel types:
 * - Zod schema: llm-service `savingsGoalSchema` in ConversationalMMIntakeSchema.ts
 * - Builder: money-map-simulator `SavingsIntakeEntry` in builders.ts (identical)
 */
export interface SavingsIntakeEntry {
    description?: string | null;
    targetAmountCents: number;
    currentAmountCents: number;
    targetDate?: string | null;
}

/**
 * Minimal intake subset passed from the LLM service to the client for
 * re-running simulations on result slides. Serialized in AhaMomentWidgetProps.
 *
 * Parallel types:
 * - Zod schema: llm-service `conversationalMMIntakeSchema` in ConversationalMMIntakeSchema.ts
 *   (superset — also has `estimatedBills`, `startInvesting`, `memberNotes`, `goalsDebt.debtTypes`)
 * - Builder input: money-map-simulator `SimulationIntake` in builders.ts
 *   (same shape, used directly to construct simulations)
 */
export interface SlideText {
    goalsIntroHeader?: string | null;
    goalsIntroBody?: string | null;
    goalsIntroFirstGoal?: string | null;
    goalsIntroTransition?: string | null;
    financialHealthHeader?: string | null;
    financialHealthBody?: string | null;
    financialHealthFooter?: string | null;
    savingsGoalsHeader?: string | null;
}

interface IntakeIncomes {
    monthlyTakeHomeCents: number;
    annualVariableIncomeCents: number;
    incomeSources: Array<{
        label: string;
        monthlyCents: number;
        cadence: string;
        rawAmountCents: number;
    }>;
}

interface IntakeBill {
    category: string;
    label: string;
    amountCents: number;
}

interface IntakeDebt {
    annualInterestRatePercent: number;
    balanceRemainingCents: number;
    description: string;
    minimumPaymentCents: number;
}

interface InstakeSavings {
    description: string;
    targetAmountCents: number;
    currentAmountCents: number;
    targetDate?: string | null;
}

export interface IntakeInvestmentMember {
    label: string;
    age: number;
}

export type ExistingAccountOwnership =
    | 'primary'
    | 'partner'
    | 'joint'
    | 'unknown';

/** Account types that can appear as actual account entries (excludes the "no existing" sentinel). */
export type ExistingAccountEntryType = Exclude<
    ExistingAccountType,
    ExistingAccountType.NoExistingInvestments
>;

export interface ExistingAccountEntry {
    accountType: ExistingAccountEntryType;
    balanceCents?: number | null;
    ownership: ExistingAccountOwnership;
}

/**
 * Fields collected/calculated during the chat.
 * Keep in sync with investmentCollectionSchema in
 * apps/llm-service/src/conversational-mm/tools/collection/investmentCollection.ts
 */
export interface ChatInvestmentData {
    isJoint: boolean;
    filingStatus?: string | null;
    members: IntakeInvestmentMember[];
    annualHouseholdPreTaxIncomeCents?: number | null;
    existingAccounts?: ExistingAccountEntry[] | null;
    plannedStartingCashContributionCents?: number | null;
}

/** Extends chat data with fields set by the results page sliders. */
export interface IntakeInvestment extends ChatInvestmentData {
    /** Set by the contribution slider; initialized to 15% of pre-tax income. */
    annualContributionCents: number;
    /** Set by the time horizon slider; initialized via computeDefaultProjectedYears. */
    projectedYears: number;
}

export interface AhaMomentIntake {
    monthlyTakeHomeCents: number;
    monthlyExpensesCents: number;
    monthlySpendCents: number;
    monthlyGoalsSpendCents: number;
    branch?: CMMBranch | null;
    slideText?: SlideText | null;
    incomes?: IntakeIncomes | null;
    bills?: IntakeBill[] | null;
    debts?: IntakeDebt[] | null;
    debtTypes?: string[] | null;
    savings?: InstakeSavings[] | null;
    investment?: IntakeInvestment | null;
    /**
     * Whether the member is planning with a partner (goals branches capture this
     * up front). Carried through to the artifact for downstream use; the money-map
     * simulator does not consume it yet.
     */
    isJoint?: boolean | null;
}

export interface AhaMomentWidgetProps {
    intake: AhaMomentIntake;
    image: AhaMomentImage;
    text: string;
    date: string;
    moneyMap: MoneyMapNumbers;
    slides: ResultSlide[];
    highInterestDebtTotalCents?: number;
    autoNavigate?: boolean;
    /** Semver of the conversational flow that produced this (see CMM_CHAT_VERSION). */
    chatVersion?: string;
    /** Semver of the artifact/results computation (see CMM_ARTIFACT_VERSION). */
    artifactVersion?: string;
}

export interface SpendGoalsWidgetProps {
    monthlyTakeHomeCents: number;
    remainingCents: number;
    confirmedMonthlySpendCents?: number;
    confirmedMonthlyGoalsSpendCents?: number;
    isConfirmed?: boolean;
}

export interface MultiSelectOption {
    id: string;
    label: string;
}

export interface MultiSelectInputWidgetProps {
    options: MultiSelectOption[];
    responseKey: string;
    promptText?: string;
    minSelections?: number;
    maxSelections?: number;
    allowCustomInput?: boolean;
    customInputPlaceholder?: string;
    preSelectedOptionIds?: string[];
}

/** How the slider's value is formatted for display. */
export type SliderInputUnit = 'currency' | 'number';

export interface SliderInputWidgetProps {
    /** Dot-path intake field populated on submit, e.g. goalsDebt.creditCardBalance. */
    responseKey: string;
    /** Question shown above the value, e.g. "About how much do you owe across all credit cards?". */
    promptText: string;
    min: number;
    max: number;
    /** Increment between ticks. Defaults to 1. */
    step?: number;
    /** Starting value the ruler centers on. Defaults to min. */
    initialValue?: number;
    /** Display formatting for the value. Defaults to 'currency'. */
    unit?: SliderInputUnit;
    /** When true, shows a pencil affordance to type an exact value. Defaults to true. */
    allowManualEntry?: boolean;
}

/** A single debt type to collect a balance for in the multi-step debt slider. */
export interface DebtBalanceSliderEntry {
    /** Debt type id, e.g. 'credit-cards'. */
    type: string;
    /** Member-facing label, e.g. 'Credit cards'. */
    label: string;
}

export interface DebtBalanceSlidersWidgetProps {
    /** Ordered list of debt types to collect balances for. */
    entries: DebtBalanceSliderEntry[];
    /** Whether the household is joint (drives "you and your partner" framing). */
    isJoint: boolean;
}

export interface IncomeCollectionWidgetProps {
    /** Whether the household is joint (drives "you and your partner" framing). */
    isJoint: boolean;
}

export interface DebtSubAccountInfo {
    name: string;
    balanceCents: number;
    annualInterestRate: number;
    completionDate: string | null;
}

export enum RuleType {
    DebtPayDown = 'debtPayDown',
    StarterEmergencyFund = 'starterEmergencyFund',
    FullEmergencyFund = 'fullEmergencyFund',
    Savings = 'savings',
    Retirement = 'retirement',
}

export interface RuleCompletionInfo {
    type: RuleType;
    description: string;
    splitPercentage: number;
    targetDisplay: string;
    completionDate: string | null;
    completionLabel: string | null;
    allocationType: string;
    subAccounts?: DebtSubAccountInfo[];
    targetBalanceCents?: number;
    monthlyContributionMaxCents?: number;
    yearlyContributionMaxCents?: number;
}

export enum RuleSetType {
    EmergencyFund = 'emergencyFund',
    FinancialHealth = 'financialHealth',
}

export interface RuleSetDisplay {
    name: string;
    type: RuleSetType;
    priority: number;
    rules: RuleCompletionInfo[];
}

/** Account kinds produced by `buildInvestmentWaterfall`. */
export type InvestmentWaterfallAccountType =
    | 'roth_ira'
    | 'backdoor_roth_ira'
    | 'brokerage';

/**
 * Slim investment-waterfall recommendation persisted on CMM results (parallel to
 * `goalCompletionDates` for debt/savings). Built client-side via
 * `buildInvestmentWaterfall` so the backend does not re-derive MAGI / backdoor.
 */
export type InvestmentWaterfallOwnership = 'primary' | 'partner' | 'joint';

export interface PersistedInvestmentWaterfall {
    useBackdoor: boolean;
    /** Waterfall order: retirement row(s), then brokerage. */
    accounts: Array<{
        accountType: InvestmentWaterfallAccountType;
        label: string;
        ownership?: InvestmentWaterfallOwnership;
    }>;
}

/**
 * The member's first-question ("what best describes you?") answer, captured
 * deterministically on the client at commit time (independent of the LLM's
 * free-text `memberNotes` summary). A menu pick carries its `optionId` + `label`;
 * a free-text ("Something else…") answer carries `freeText`. A `money-goal`
 * deep link is resolved to the matching option and stored the same way.
 */
export interface WelcomeSelection {
    /** Welcome menu option id (e.g. 'get-out-of-debt'); null for a free-text answer. */
    optionId?: string | null;
    /** Display label for the picked option (e.g. 'Get out of debt'); null for free-text. */
    label?: string | null;
    /** Free-text the member typed ("Something else…"); null for a menu pick. */
    freeText?: string | null;
}

export interface OnboardingChatResults {
    moneyMap: {
        incomeTotalCents: number;
        billsTotalCents: number;
        spendTotalCents: number;
        goalsTotalCents: number;
    };
    artifactData: {
        version?: number;
        debtFreeDate: string;
        slides: string;
        image: AhaMomentImage;
    };
    rawChatResults: AhaMomentIntake;
    highInterestDebtTotalCents?: number;
    goalCompletionDates?: Partial<Record<ResultSlide, RuleSetDisplay[]>>;
    investmentWaterfall?: PersistedInvestmentWaterfall;
    /**
     * The member's first-question answer (menu pick id+label or free-text),
     * captured deterministically client-side. Carried onto the artifact.
     */
    welcomeSelection?: WelcomeSelection;
    /**
     * Semver of the conversational flow that produced this result (see
     * CMM_CHAT_VERSION). Distinct from `artifactData.version`, which is a
     * renderer discriminator (legacy vs. new results UI), not a change stamp.
     */
    chatVersion?: string;
    /** Semver of the artifact/results computation (see CMM_ARTIFACT_VERSION). */
    artifactVersion?: string;
}

interface BaseAhaResult {
    displayHeadline: string;
    image: AhaMomentImage;
    moneyMap: MoneyMapNumbers;
}

export interface DebtAhaResult extends BaseAhaResult {
    type: 'debt';
    debtFreeDate: string;
    highInterestDebtTotalCents?: number;
}

export interface SavingsAhaResult extends BaseAhaResult {
    type: 'savings';
    debtFreeDate: string;
}

export interface InvestingAhaResult extends BaseAhaResult {
    type: 'investing';
    projectedTotalCents: number;
    projectedDate: string;
}

export type AhaResult = DebtAhaResult | SavingsAhaResult | InvestingAhaResult;

export enum OnboardingChatResultsTooltipType {
    MoneyMap = 'moneyMap',
    SpendGoalsSlider = 'spendGoalsSlider',
    StarterEmergencyFund = 'starterEmergencyFund',
    FinancialHealthSet = 'financialHealthSet',
    DebtPayDown = 'debtPayDown',
    Savings = 'savings',
}

export type OnboardingChatResultsTooltipData = Record<
    OnboardingChatResultsTooltipType,
    {
        title: string;
        description: string;
    }
>;

export enum OnboardingChatModule {
    Savings = 'savings',
    DebtCheck = 'debt-check',
    Debt = 'debt',
    Income = 'income',
    PreTaxIncome = 'pre-tax-income',
    CoreNumbers = 'core-numbers',
}
